import { useState, useRef, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
  PROCESSING_MEDIA_TO_USE,
  SAVING_MEDIA,
  GALLERY_SOURCE_SOCIAL_POSTS,
  GALLERY_SOCIAL_POSTS_DESCRIPTION,
  INSTAGRAM,
  FACEBOOK,
  PARTNERSHIP,
  INSTAGRAM_ACCOUNT_PLACEHOLDER,
  PAGE_FACEBOOK_PLACEHOLDER,
  FIELD_REQUIRED,
  SELECT_ACCOUNT_TO_LOAD_GALLERY_CONTENT,
  INFORMATION,
  SELECT_CONTENT_CONFIRMATION_TEXT,
} from "../../i18n/keysTranslations";
import { ConfirmAlert } from "../Alerts/Alerts";
import { BUTTON_SHADOW } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp } from "../../lib/redux";
import { useDebounced } from "../../hooks/useDebounce";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Button from "../Buttons/Button";
import Gallery from "../Gallery/Gallery";
import { usePagination } from "../../hooks/usePagination";
import {
  META,
  ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
  ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST,
  ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST,
  BORDER_RADIUS_BUTTON,
} from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { startGetGalleryAdsPlatforms } from "../../actions/adsCampaigns";
import {
  startAddContentsToList,
  startSaveMediaInStorage,
} from "../../actions/user";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import Select from "../Form/Select";
import SelectInstagramAccountWithConnect from "../Meta/SelectInstagramAccountWithConnect";
import SelectFacebookPageWithConnect from "../Meta/SelectFacebookPageWithConnect";
import { useForm } from "react-hook-form";
import { useMetaAccountsLists } from "../../hooks/useMetaAccountsLists";

const PAGE_SIZE = 10;

const ModalGalleryAdsPlatforms = ({
  modalOpen,
  multiple = true,
  onCloseModal,
  onCallbackSelectedItems,
  socialNetworkURL,
  pathStorage,
  contentFolderID = null,
  compact = false,
  from = ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
  forceSocialPostsSource = false,
  integrationID: integrationIDForced,
  adAccountID: adAccountIDForced,
  pageID: pageIDForced,
}) => {
  const [data, setData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  const { t } = useTranslationApp();

  const galleryRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dataRef = useRef(data);
  const selectedItemsRef = useRef(selectedItems);
  const isUploadingRef = useRef(isUploading);
  dataRef.current = data;
  selectedItemsRef.current = selectedItems;
  isUploadingRef.current = isUploading;

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "100px",
    once: false,
    disabledTimeout: true,
  });

  const dispatch = useDispatchApp();
  const { uniqueInstagramBusinessList, uniqueFacebookPagesList } =
    useMetaAccountsLists();
  const { totalTopOffsetPx } = useRebrandBanner();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      metaFilter: from || ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
      selectedInstagramAccount: null,
      selectedFacebookPage: null,
    },
  });
  const metaFilter = watch("metaFilter");
  const selectedInstagramAccount = watch("selectedInstagramAccount");
  const selectedFacebookPage = watch("selectedFacebookPage");
  const isPicker = !multiple;

  const isInstagramFilter =
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST;
  const isFacebookOrPartnershipFilter =
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST ||
    metaFilter === ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST;

  const getSelectedAccountParams = () => {
    if (
      forceSocialPostsSource &&
      integrationIDForced &&
      adAccountIDForced &&
      pageIDForced
    ) {
      return {
        integrationID: integrationIDForced,
        adAccountID: adAccountIDForced,
        pageID: pageIDForced,
      };
    }
    if (isInstagramFilter && selectedInstagramAccount) {
      const fullAccount = uniqueInstagramBusinessList.find(
        (a) => a.instagramID === selectedInstagramAccount.instagramID
      );
      return {
        integrationID: selectedInstagramAccount.integrationID,
        adAccountID: fullAccount?.adAccountID,
        pageID: selectedInstagramAccount.pageID,
      };
    }
    if (isFacebookOrPartnershipFilter && selectedFacebookPage) {
      const fullPage = uniqueFacebookPagesList.find(
        (p) => p.id === selectedFacebookPage.id
      );
      return {
        integrationID: selectedFacebookPage.integrationID,
        adAccountID: fullPage?.adAccountID,
        pageID: selectedFacebookPage.id,
      };
    }
    return null;
  };

  const getInitialData = async (signal) => {
    const params = getSelectedAccountParams();
    if (!params) {
      setLoading(false);
      setData([]);
      setSelectedItems([]);
      return;
    }

    setLoading(true);
    setData([]);
    setSelectedItems([]);

    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform: META,
        integrationID: params.integrationID,
        adAccountID: params.adAccountID,
        pageID: params.pageID,
        socialNetworkURL,
        lastKey: undefined,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: metaFilter,
        signal,
      })
    );
    if (response?.aborted) return;

    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData(dataFormatted);
    }

    setLoading(false);
  };

  const getNextMedia = useDebounced(async () => {
    if (loadLastItem || isCalling) return;

    const params = getSelectedAccountParams();
    if (!params) return;

    const signal = abortControllerRef.current?.signal;

    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform: META,
        integrationID: params.integrationID,
        adAccountID: params.adAccountID,
        pageID: params.pageID,
        socialNetworkURL,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: metaFilter,
        signal,
      })
    );
    if (response?.aborted) return;
    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData((prevState) => [...prevState, ...dataFormatted]);
    }
  }, 1000);

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: getNextMedia,
  });

  useEffect(() => {
    abortControllerRef.current?.abort();
    setLoadLastItem(false);
    setData([]);
    setSelectedItems([]);
    setValue("selectedInstagramAccount", null);
    setValue("selectedFacebookPage", null);
    setLoading(false);
    //eslint-disable-next-line
  }, [metaFilter]);

  useEffect(() => {
    if (
      !forceSocialPostsSource ||
      !integrationIDForced ||
      !pageIDForced ||
      !modalOpen
    ) {
      return;
    }
    const isInstagram = metaFilter === ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST;
    const isFacebookOrPartnership =
      metaFilter === ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST ||
      metaFilter === ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST;

    if (isInstagram) {
      const account = uniqueInstagramBusinessList.find(
        (a) =>
          a.integrationID === integrationIDForced && a.pageID === pageIDForced
      );
      const value = account
        ? {
            id: account.integrationID,
            name: account.name,
            integrationID: account.integrationID,
            instagramID: account.instagramID,
            pageID: account.pageID,
          }
        : null;
      setValue("selectedInstagramAccount", value);
      setValue("selectedFacebookPage", null);
    } else if (isFacebookOrPartnership) {
      const page = uniqueFacebookPagesList.find(
        (p) => p.integrationID === integrationIDForced && p.id === pageIDForced
      );
      const value = page
        ? {
            id: page.id,
            name: page.name,
            integrationID: page.integrationID,
            imageURL: page.imageURL,
          }
        : null;
      setValue("selectedFacebookPage", value);
      setValue("selectedInstagramAccount", null);
    }
    //eslint-disable-next-line
  }, [
    forceSocialPostsSource,
    integrationIDForced,
    pageIDForced,
    modalOpen,
    metaFilter,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
  ]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    const params = getSelectedAccountParams();
    if (!params) {
      setLoading(false);
      setData([]);
      setSelectedItems([]);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    getInitialData(controller.signal);

    return () => {
      controller.abort();
    };
    //eslint-disable-next-line
  }, [selectedInstagramAccount, selectedFacebookPage]);

  useEffect(() => {
    if (isNearScreen && !loadLastItem) getNextMedia();
    //eslint-disable-next-line
  }, [isNearScreen]);

  const onFailedLoadUrl = (index) => {
    setData((prevState) => {
      const newData = [...prevState];
      const itemToRemove = newData[index];
      if (itemToRemove?.id) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter((id) => id !== itemToRemove.id)
        );
      }
      const filteredData = newData.filter((item, i) => i !== index);
      return filteredData;
    });
  };

  const onSelectItem = (index) => {
    const item = data[index];
    if (!item || !item.id) return;

    const itemId = item.id;

    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        if (multiple) {
          return [...prevSelected, itemId];
        } else {
          return [itemId];
        }
      }
    });
  };

  const onSendSelectedItems = async (indexes) => {
    const currentData = dataRef.current;
    const currentSelectedItems = selectedItemsRef.current;
    const dataSelectedItems = [];
    const itemsToProcess = indexes
      ? indexes.map((index) => currentData[index]).filter(Boolean)
      : currentSelectedItems
          .map((id) => currentData.find((item) => item.id === id))
          .filter(Boolean);

    if (itemsToProcess.length === 0) {
      return false;
    }

    itemsToProcess.forEach((item) => {
      if (item) {
        dataSelectedItems.push({
          url: item.url,
          from: item.from || "gallery",
          platform: item.platform || null,
          id: item.id,
          userID: item.userID || "",
          isCarousel: item.isCarousel === true,
          carouselImages: item.carouselImages || null,
        });
      }
    });

    const urls = dataSelectedItems.map((item) => item.url).filter(Boolean);

    if (urls.length === 0) {
      return false;
    }

    setIsUploading(true);
    const response = await dispatch(
      startSaveMediaInStorage({
        urls,
        path: pathStorage,
      })
    );

    if (!response.ok) {
      setIsUploading(false);
      return false;
    }

    const urlsStored = response.data.urls;

    urlsStored.forEach((urlStored, index) => {
      if (!urlStored || !dataSelectedItems[index]) return;
      dataSelectedItems[index].url = urlStored;
    });
    await dispatch(
      startAddContentsToList(
        urlsStored.filter(Boolean).map((url) => ({
          url,
          type: "upload",
          ...(contentFolderID && { folderID: contentFolderID }),
        }))
      )
    );

    setIsUploading(false);

    await onCallbackSelectedItems(dataSelectedItems);
    onCloseModal();
    return true;
  };

  const onSelectItemPicker = async (index) => {
    if (isUploadingRef.current) return;
    const item = dataRef.current[index];
    if (!item?.id) return;

    const result = await ConfirmAlert({
      title: t(INFORMATION),
      text: t(SELECT_CONTENT_CONFIRMATION_TEXT),
    });
    if (!result?.isConfirmed) return;

    await onSendSelectedItems([index]);
  };

  const dataFiltered = data;

  const selectedItemsAsIndexes = useMemo(() => {
    return dataFiltered
      .map((item, index) =>
        item?.id && selectedItems.includes(item.id) ? index : null
      )
      .filter((index) => index !== null);
  }, [dataFiltered, selectedItems]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={compact ? true : !compact}
      fullScreen={compact ? false : !compact}
      maxWidth={compact ? "lg" : "xl"}
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxContent={{
        backgroundColor: "fields.background",
        p: 0,
        pl: 3,
        pb: 2,
        mt: 1,
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
      refContent={galleryRef}
      title={t(GALLERY_SOURCE_SOCIAL_POSTS)}
      description={t(GALLERY_SOCIAL_POSTS_DESCRIPTION)}
    >
      <Box
        sx={{
          mb: 2,
          pt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <Select
            name="metaFilter"
            variant="filled"
            control={control}
            doTranslate={true}
            options={[
              {
                value: ADS_CAMPAIGN_MEDIA_FROM_INSTAGRAM_POST,
                label: INSTAGRAM,
              },
              { value: ADS_CAMPAIGN_MEDIA_FROM_FACEBOOK_POST, label: FACEBOOK },
              {
                value: ADS_CAMPAIGN_MEDIA_FROM_PARTNERSHIP_POST,
                label: PARTNERSHIP,
              },
            ]}
            sx={{
              borderRadius: BORDER_RADIUS_BUTTON,
              width: 360,
              maxWidth: 360,
            }}
          />
          {isInstagramFilter && (
            <SelectInstagramAccountWithConnect
              name="selectedInstagramAccount"
              variant="filled"
              label={t(INSTAGRAM_ACCOUNT_PLACEHOLDER)}
              fullWidth
              control={control}
              errors={errors}
              uniqueInstagramBusinessList={uniqueInstagramBusinessList}
              skipAutoSelect={true}
              disabled={forceSocialPostsSource}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              sxTextField={{
                width: 360,
                maxWidth: 360,
              }}
            />
          )}
          {isFacebookOrPartnershipFilter && (
            <SelectFacebookPageWithConnect
              name="selectedFacebookPage"
              variant="filled"
              label={t(PAGE_FACEBOOK_PLACEHOLDER)}
              fullWidth
              control={control}
              errors={errors}
              uniqueFacebookPagesList={uniqueFacebookPagesList}
              skipAutoSelect={true}
              disabled={forceSocialPostsSource}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
              sxTextField={{
                width: 360,
                maxWidth: 360,
              }}
            />
          )}
        </Box>
      </Box>
      {!getSelectedAccountParams() && (
        <Box
          sx={{
            minHeight: 200,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-12px",
          }}
        >
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ fontSize: 18 }}
          >
            {t(SELECT_ACCOUNT_TO_LOAD_GALLERY_CONTENT)}
          </Typography>
        </Box>
      )}
      {loading && (
        <Loader size={70} hasMessage={true} message={t(LOADING_CONTENT)} />
      )}
      {!loading && getSelectedAccountParams() && (
        <Gallery
          data={dataFiltered}
          onCloseModal={onCloseModal}
          lastItemRef={fromRef}
          loadLastItem={loadLastItem}
          onFailedLoadUrl={onFailedLoadUrl}
          onSelectItem={isPicker ? onSelectItemPicker : onSelectItem}
          selectedItems={selectedItemsAsIndexes}
          onSelectPreview={() => {}}
          multiple={multiple}
          overlayPickerMode={isPicker}
          showFiltersSelector={false}
          showActionButtons={false}
          sxGallery={{
            px: 0,
          }}
          from={"posts"}
          acceptedFormats={[]}
          showDimensionsOverlay={true}
        />
      )}
      {!loading &&
        getSelectedAccountParams() &&
        dataFiltered?.length === 0 &&
        loadLastItem && (
          <Box
            sx={{
              height: `calc(100% - ${totalTopOffsetPx}px)`,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "-12px",
            }}
          >
            <Typography variant="h4" align="center">
              {t(MODAL_GALLERY_EMPTY_CONTENT)}
            </Typography>
          </Box>
        )}

      {(selectedItems.length > 0 || isUploading) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: isUploading ? "#FFF" : "transparent",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            py: 1,
            mt: 2,
            pb: 2,
          }}
        >
          {isUploading && (
            <LinearLoaderWithTime
              labels={[t(PROCESSING_MEDIA_TO_USE), t(SAVING_MEDIA)]}
              time={30 * 1000}
            />
          )}

          {!isUploading && !isPicker && (
            <Button
              color="primary"
              sx={{
                boxShadow: BUTTON_SHADOW,
                "&:hover": {
                  boxShadow: BUTTON_SHADOW,
                },
              }}
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                onSendSelectedItems();
              }}
            >
              {t(BUTTON_ADD_SELECTED_CONTENT)}
            </Button>
          )}
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ModalGalleryAdsPlatforms;
